import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, istDay } from "@/lib/api";

export default function SlotPicker({ doctorId, coDoctorId, value, onChange, days = 14 }) {
  const dates = useMemo(() => Array.from({ length: days }, (_, i) => new Date(Date.now() + i * 86400000)), [days]);
  const [day, setDay] = useState(istDay(dates[0]));
  const [slots, setSlots] = useState(null);
  useEffect(() => {
    setSlots(null);
    const get = (id) => api.get(`/doctors/${id}/slots`, { params: { day } }).then((r) => r.data);
    Promise.all([get(doctorId), coDoctorId ? get(coDoctorId) : null]).then(([a, b]) => {
      if (!b) return setSlots(a);
      const free = new Set(b.map((x) => new Date(x.start_at).getTime()));  // joint: only times free for both doctors
      setSlots(a.filter((x) => free.has(new Date(x.start_at).getTime())));
    }).catch(() => setSlots([]));
  }, [doctorId, coDoctorId, day]);
  return (
    <div data-testid="slot-picker" className="min-w-0 max-w-full overflow-hidden">
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
        {dates.map((d) => {
          const key = istDay(d);
          const active = key === day;
          return (
            <button key={key} type="button" onClick={() => setDay(key)} data-testid={`slot-day-${key}`}
              className={`shrink-0 w-16 rounded-xl border py-2 text-center transition-colors ${active ? "border-[#2412B5] bg-[#2412B5] text-white" : "border-[#E2E8ED] bg-white hover:border-[#2412B5]/50"}`}>
              <span className="block text-[10px] uppercase tracking-wider opacity-80">{d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: "short" })}</span>
              <span className="block text-lg font-semibold font-display">{d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit" })}</span>
              <span className="block text-[10px] opacity-80">{d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", month: "short" })}</span>
            </button>
          );
        })}
      </div>
      <div className="mt-4 min-h-[64px]">
        {slots === null ? <Loader2 className="h-5 w-5 animate-spin text-[#086B2D]" /> : slots.length === 0 ? (
          <p className="text-sm muted" data-testid="no-slots">No available slots on this day. Try another date.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {slots.map((s) => (
              <button key={s.start_at} type="button" onClick={() => onChange(s.start_at)} data-testid={`slot-${s.label.replace(/[\s:]/g, "")}`}
                className={`rounded-lg border py-2 text-sm transition-colors ${value === s.start_at ? "border-[#086B2D] bg-[#086B2D] text-white font-semibold ring-2 ring-[#086B2D]/30" : "border-[#E2E8ED] bg-white hover:border-[#086B2D]/60"}`}>
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
